import { createApp, nextTick } from "vue";
import { createI18n } from "vue-i18n";
import axios from "axios";
import VueAxios from "vue-axios";
import { alertError } from "@/composables/ui/useDialogs";
import "bootstrap";
import "@/scss/tokens.scss";

import App from "@/App.vue";
import router from "@/router";

import messages from "@/messages";

import {Token} from "shared/Token";
import { isAuthCallbackPath, sendToLogin } from "@/services/login";

// clientside token authentication
function gup(name: string, url: string) {
  if (!url) url = location.href;
  name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
  var regexS = "[\\?&]" + name + "=([^&#]*)";
  var regex = new RegExp(regexS);
  var results = regex.exec(url);
  return results == null ? null : results[1];
}

// set token clientside
let urlToken = gup("token", location.href);
if (urlToken) {
  window.localStorage.setItem("userToken", urlToken);
  const url = new URL(window.location.href);
  url.searchParams.delete("token");
  window.location.href = url.toString();
}

const isCEF = gup("isCEF", location.href)
if (isCEF) {
  window.localStorage.setItem("isCEF", "true");
}

const isLauncher = gup("isLauncher", location.href)
if (isLauncher === "true") {
  window.localStorage.setItem("isLauncher", "true");
}

let appLocked = false;
// lock system.
document.addEventListener("keydown", (e) => {
  if (e.key == "Escape" && !appLocked) {
    // @ts-ignore
    document.activeElement?.blur();
    appLocked = true;
  }

  if (appLocked) {
    e.preventDefault();
  }
});

document.addEventListener("keypress", (e) => {
  // dont consume key events on app locked
  if (appLocked) {
    e.preventDefault();
  }
});

// unlock on mouse over
document.addEventListener("mouseover", (e) => {
  if (appLocked) {
    // @ts-ignore
    document.activeElement?.blur();
    appLocked = false;
  }
});

declare module "axios" {
  export interface AxiosRequestConfig {
    skipSwal?: boolean;
    skipDeduplication?: boolean;
  }
}

axios.defaults.baseURL = "/api/";

/** Default timeout so requests don't hang indefinitely (avoids stuck UI state e.g. isSelectingTicket) */
axios.defaults.timeout = 30000;

axios.defaults.headers.common = {
  authorization: "Bearer " + window.localStorage.getItem("userToken"),
};

axios.defaults.validateStatus = (status) => {
  return status >= 200 && status < 500;
};

axios.interceptors.response.use(
  (res) => {
    if (res.config.skipSwal) return res.data;
    if (res.status > 299) {
      if (res.status == 401) {
        // The callback page handles its own failures (and retry limit).
        if (!isAuthCallbackPath()) sendToLogin();
        return;
      }

      const message = res.data.error || "Unexpected status code: " + res.status;
      //alert(message)
      alertError(message, { title: getErrorTitle() });
    }
    return res.data || undefined;
  },
  (err) => {
    if (err.code === "ECONNABORTED" && err.config && !err.config.skipSwal) {
      const timeoutMessage = typeof getRequestTimeoutMessage === "function" ? getRequestTimeoutMessage() : "Request timed out. Please check your connection and try again.";
      alertError(err.message || timeoutMessage, { title: getErrorTitle() });
    }
    return Promise.reject(err);
  }
);

/** Set after i18n is created so interceptor swal can use translations */
let getRequestTimeoutMessage: () => string = () => "Request timed out. Please check your connection and try again.";
let getErrorTitle: () => string = () => "Error";

/* return if token does not exist
let tokenFromLS = window.localStorage.getItem("userToken"); 
if (!tokenFromLS) {
   //alert("No token suppied")
   axios.get("/login").then(res => {
      // @ts-ignore
      location.href = res.loginURL
   })
} else {
   // set global object containing token
   let decoded = jwt_decode(tokenFromLS);
   if (decoded) {
      // @ts-ignore
      window.userObject = decoded as IUser;
   } else {
      axios.get("/login").then(res => {
         // @ts-ignore
         location.href = res.loginURL
      })
      //location.reload();
   }
}
*/

// get token for iframes validate
async function getToken() {
   try {
       const response = (await axios.get('/tokenFrame')) as Token;
       if (response == undefined) {
          throw new Error('Failed to get token');
       }
       const token = response.token;
       localStorage.setItem('tokenFrame', token);
   } catch (error) {
       console.error('Error getting token:', error);
   }
 }
 // No session yet on the authorization-code callback page.
 if (!isAuthCallbackPath()) getToken();

const i18n = createI18n({
  locale: "es", // set locale
  fallbackLocale: "es", // set fallback locale
  messages,
});

getRequestTimeoutMessage = () => i18n.global.t("requestTimeout");
getErrorTitle = () => i18n.global.t("error");

export const { t } = i18n.global;

const app = createApp(App);

// Global error handler to catch component creation errors
app.config.errorHandler = (err, instance, info) => {
  if (!urlToken) window.dispatchEvent(new Event('joebill:appFailed'));
  console.error('Vue Global Error Handler:', err);
  console.error('Error Info:', info);
  console.error('Component Instance:', instance);
  if (err instanceof Error) {
    console.error('Error Stack:', err.stack);
  }
};

app
  .use(router)
  .use(i18n)
  //.directive("click-outside", singletonClickOutside)
  .use(VueAxios, axios)
  .mount("#app");

// The token-bearing entry redirects first; only its final mounted route can be ready.
if (!urlToken) {
  void router.isReady().then(() => nextTick()).then(() => {
    window.dispatchEvent(new Event('joebill:appMounted'));
  }, () => window.dispatchEvent(new Event('joebill:appFailed')));
}
